import React, { useMemo, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './CaseStudy.css';
import './CaseStudyEditorial.css';
import './CaseStudyMinimal.css';
import './SiteSidebar.css';
import { WORK_ITEMS } from './workData';
import GlassImage from './GlassImage';

function getOverviewParagraphs(c) {
  const paragraphs = [];
  if (c.context) paragraphs.push(c.context);
  else if (c.problem) paragraphs.push(c.problem);
  if (c.overview) paragraphs.push(c.overview);
  return paragraphs;
}

function uniqueParagraphs(items) {
  return items.filter((item, index) => item && items.indexOf(item) === index);
}

function mediaSrc(src) {
  if (!src) return '';
  if (src.startsWith('http') || src.startsWith('//')) return src;
  return process.env.PUBLIC_URL + src;
}

function figureKind(img, isVideo) {
  if (typeof img === 'object' && img.fit) return img.fit;
  const src = typeof img === 'object' ? img.src : img;
  if (isVideo) return 'video';
  if (src && /\/slides-/.test(src)) return 'slide';
  return 'shot';
}

function CaseFigure({ src, caption, label, compact = false }) {
  if (!src) return null;

  let resolved;
  let cap = caption;
  let isCompact = compact;
  if (typeof src === 'object') {
    resolved = src.src?.startsWith('http') ? src.src : mediaSrc(src.src);
    cap = cap || src.caption;
    isCompact = compact || !!src.compact;
  } else {
    resolved = src.startsWith('http') ? src : mediaSrc(src);
  }

  const isVideo = resolved.endsWith('.mp4');

  return (
    <figure className={`cs-figure${isCompact ? ' cs-figure--compact' : ''}`}>
      {label && <span className="cs-figure-label">{label}</span>}
      <GlassImage src={resolved} video={isVideo} />
      {cap && <figcaption>{cap}</figcaption>}
    </figure>
  );
}

function ImageCarousel({ images }) {
  const doubled = [...images, ...images];

  return (
    <div className="cs-carousel">
      <div className="cs-carousel-track">
        {doubled.map((img, i) => (
          <figure key={`${img.src || ''}-${i}`} className="cs-carousel-slide cs-image-canvas">
            <GlassImage src={mediaSrc(img.src)} />
          </figure>
        ))}
      </div>
    </div>
  );
}

/* One fixed text container for every case study:
   eyebrow + title on the left, running text on the right. */
function TextRow({ eyebrow, title, children }) {
  const hasBody = React.Children.toArray(children).length > 0;
  if (!eyebrow && !title && !hasBody) return null;
  return (
    <div className="cs-row">
      <div className="cs-row-head">
        {eyebrow && <p className="cs-eyebrow">{eyebrow}</p>}
        {title && <h2 className="cs-row-title">{title}</h2>}
      </div>
      {hasBody && <div className="cs-row-body">{children}</div>}
    </div>
  );
}

function WorkBlock({ lead, body }) {
  return (
    <div className="cs-work-block">
      {lead && <p className="cs-work-lead">{lead}</p>}
      {body && <p className="cs-p">{body}</p>}
    </div>
  );
}

function IssuesIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden="true">
      <circle cx="20" cy="20" r="16" stroke="#9b7fe0" strokeWidth="2" />
      <path d="M20 12v10" stroke="#9b7fe0" strokeWidth="2" strokeLinecap="round" />
      <circle cx="20" cy="27" r="1.5" fill="#9b7fe0" />
    </svg>
  );
}

function SolutionIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden="true">
      <path
        d="M20 6l2.2 6.8H29l-5.6 4.1 2.1 6.8L20 19.6l-5.5 4.1 2.1-6.8L11 12.8h6.8L20 6z"
        stroke="#9b7fe0"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path d="M30 8v3M31.5 9.5H28.5" stroke="#9b7fe0" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M33 14v2M34 15H32" stroke="#9b7fe0" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function PenNibIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 2.5L5.5 19.5l6.5-3 6.5 3L12 2.5z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path d="M12 2.5v12.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="12" cy="6.5" r="1" fill="currentColor" />
    </svg>
  );
}

function EyeSlashIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M3 12s3.5-6 9-6 9 6 9 6-3.5 6-9 6-9-6-9-6z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="2.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M4 4l16 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function FeatureCardIcon({ icon }) {
  if (icon === 'pen') return <PenNibIcon />;
  if (icon === 'eye-slash') return <EyeSlashIcon />;
  return null;
}

function IssueSolutionBlock({ type, title, items }) {
  if (!items?.length) return null;
  return (
    <div className="cs-issue-solution">
      <div className="cs-issue-solution-icon" aria-hidden="true">
        {type === 'issues' ? <IssuesIcon /> : <SolutionIcon />}
      </div>
      <div className="cs-issue-solution-content">
        <h3 className="cs-issue-solution-title">{title}</h3>
        <ul className="cs-issue-solution-list">
          {items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function scrollToId(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

const EDITORIAL_NAV = [
  { id: 'impact', label: 'Impact' },
  { id: 'overview', label: 'Overview' },
  { id: 'problem', label: 'Problem' },
  { id: 'exploring', label: 'Exploring' },
  { id: 'roadmap', label: 'Roadmap' },
  { id: 'solutions', label: 'Solutions' },
];

function useActiveSection(ids) {
  const [activeNav, setActiveNav] = useState(ids[0] || '');

  useEffect(() => {
    if (!ids.length) return undefined;

    const onScroll = () => {
      let current = ids[0];
      for (const id of ids) {
        const el = document.getElementById(id);
        if (!el) continue;
        if (el.getBoundingClientRect().top <= 120) current = id;
      }
      setActiveNav(current);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [ids]);

  return activeNav;
}

function CaseStudySidebar({ navigate, project, c }) {
  const navItems = c.editorialNav || EDITORIAL_NAV;
  const ids = useMemo(() => navItems.map((n) => n.id), [navItems]);
  const activeNav = useActiveSection(ids);

  return (
    <aside className="cs-rail" aria-label="Case study navigation">
      <button
        type="button"
        className="cs-rail-back"
        onClick={() => navigate('/')}
      >
        ← Back
      </button>

      <nav className="cs-rail-nav" aria-label="Case study sections">
        {navItems.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`cs-rail-link${activeNav === item.id ? ' cs-rail-link--active' : ''}`}
            onClick={() => scrollToId(item.id)}
          >
            {item.label}
          </button>
        ))}
      </nav>

      {(c.roles || c.timeline || c.team) && (
        <dl className="cs-rail-meta">
          {c.roles && (
            <div className="cs-rail-meta-block">
              <dt>Role</dt>
              <dd>{c.roles}</dd>
            </div>
          )}
          {c.timeline && (
            <div className="cs-rail-meta-block">
              <dt>Timeline</dt>
              <dd>{c.timeline}</dd>
            </div>
          )}
          {c.team && (
            <div className="cs-rail-meta-block">
              <dt>Team</dt>
              <dd>{c.team}</dd>
            </div>
          )}
        </dl>
      )}
    </aside>
  );
}

function getImpactBlocks(project, c) {
  if (c.impactNotes?.length > 0) {
    return c.impactNotes.map((text, index) => ({
      title: index === 0 ? 'Outcome' : `Note ${String(index + 1).padStart(2, '0')}`,
      text,
    }));
  }

  if (c.highlights?.length > 0) {
    return c.highlights.map((item) => ({
      title: item.label.replace(/^\d+\s*/, ''),
      text: item.text,
    }));
  }

  const fallbacks = [];

  if (project.shipped || /shipped/i.test(project.concept || '')) {
    fallbacks.push({
      title: 'Outcome',
      text: c.summary || project.desc,
    });
  } else if (c.summary || project.desc) {
    fallbacks.push({
      title: 'Outcome',
      text: c.summary || project.desc,
    });
  }

  if (c.screenDesignNote) {
    fallbacks.push({
      title: 'System',
      text: c.screenDesignNote,
    });
  }

  return fallbacks;
}

/* Where a pin's note goes: beside its first area if there is room,
   otherwise below or above it. All values are % of the picture. */
function notePlacement([x, y, w, h]) {
  if (x + w <= 62) return { side: 'right', left: x + w, top: y + h / 2 };
  if (x >= 38) return { side: 'left', left: x, top: y + h / 2 };
  if (y + h <= 72) return { side: 'below', left: x + w / 2, top: y + h };
  return { side: 'above', left: x + w / 2, top: y };
}

/* A screenshot with numbered pins. Hovering (or tapping) a pin dims the
   picture except the areas it points at, and shows its note beside them.
   Pin x/y and each area [x, y, w, h] are percentages of the picture. */
function AnnotatedShot({ src, alt = '', label, pins = [] }) {
  const [active, setActive] = useState(null);
  const pin = active === null ? null : pins[active];
  const note = pin ? notePlacement(pin.areas[0]) : null;
  const maskId = `cs-dim-${src.replace(/[^a-z0-9]/gi, '-')}`;

  return (
    <figure className="cs-annotated">
      {label && <span className="cs-figure-label">{label}</span>}
      <div className={`cs-annotated-shot${pin ? ' is-focused' : ''}`}>
        <img src={mediaSrc(src)} alt={alt} />

        <svg className="cs-annotated-dim" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
          <defs>
            <mask id={maskId}>
              <rect width="100" height="100" fill="#fff" />
              {pin?.areas.map(([x, y, w, h]) => (
                <rect key={`${x}-${y}`} x={x} y={y} width={w} height={h} fill="#000" />
              ))}
            </mask>
          </defs>
          <rect width="100" height="100" mask={`url(#${maskId})`} />
        </svg>

        {pin?.areas.map(([x, y, w, h]) => (
          <span
            key={`${x}-${y}`}
            className="cs-annotated-area"
            style={{ left: `${x}%`, top: `${y}%`, width: `${w}%`, height: `${h}%` }}
          />
        ))}

        {pins.map((p, i) => (
          <button
            key={p.text}
            type="button"
            className={`cs-annotated-pin${active === i ? ' is-active' : ''}`}
            style={{ left: `${p.x}%`, top: `${p.y}%` }}
            aria-label={`${i + 1}. ${p.text}`}
            onPointerEnter={(e) => { if (e.pointerType === 'mouse') setActive(i); }}
            onPointerLeave={(e) => { if (e.pointerType === 'mouse') setActive(null); }}
            onFocus={() => setActive(i)}
            onBlur={() => setActive(null)}
            onClick={() => setActive(active === i ? null : i)}
          >
            {i + 1}
          </button>
        ))}

        {pin && (
          <p
            className={`cs-annotated-note cs-annotated-note--${note.side}`}
            style={{ left: `${note.left}%`, top: `${note.top}%` }}
            role="status"
          >
            {pin.text}
          </p>
        )}
      </div>
    </figure>
  );
}

function ProcessSection({ section, hideImageLabels = false, defaultMediaFirst = false }) {
  const hasMedia = (section.images?.length || 0) > 0;
  const mediaFirst = (section.mediaFirst ?? defaultMediaFirst) && hasMedia;
  const mediaOnly = hasMedia
    && !section.title
    && !section.heading
    && !(section.body?.length)
    && !(section.challenges?.length)
    && !(section.issueItems?.length)
    && !(section.solutionItems?.length)
    && !(section.bullets?.length);
  const renderBody = () => (
    section.body?.map((para, i) => (
      <p key={i} className="cs-p">{para}</p>
    ))
  );
  const renderMedia = () => (
    section.carousel && section.images?.length > 1 ? (
      <ImageCarousel images={section.images} />
    ) : section.imageLayout === 'compare' && section.images?.length >= 2 ? (
      <div className="cs-image-canvas cs-image-compare">
        {section.images.map((img, i) => {
          const imgSrc = typeof img === 'object' ? img.src : img;
          const label = hideImageLabels ? null : (typeof img === 'object' ? img.label : null);
          const resolved = imgSrc?.startsWith('http') ? imgSrc : mediaSrc(imgSrc);
          const isVideo = resolved?.endsWith('.mp4');
          return (
            <div key={imgSrc || i} className="cs-image-compare-cell">
              {label && <span className="cs-figure-label">{label}</span>}
              <GlassImage src={resolved} video={isVideo} />
            </div>
          );
        })}
      </div>
    ) : (
      section.images?.map((img, i) => {
        const imgSrc = typeof img === 'object' ? img.src : img;
        const label = hideImageLabels ? null : (typeof img === 'object' ? img.label : null);
        const caption = hideImageLabels ? null : (typeof img === 'object' ? img.caption : null);
        const canvasColor = typeof img === 'object' ? img.canvasColor : null;
        const noCanvasBg = typeof img === 'object' ? img.noCanvasBg : false;
        const canvasVariant = typeof img === 'object' ? img.canvasVariant : null;
        const align = typeof img === 'object' ? img.align : null;
        const resolved = imgSrc?.startsWith('http') ? imgSrc : mediaSrc(imgSrc);
        const isVideo = resolved?.endsWith('.mp4');
        const kind = figureKind(img, isVideo);
        return (
          <figure
            key={imgSrc || i}
            className={`cs-figure${align === 'end' ? ' cs-figure--end' : ''} cs-figure--${kind}`}
          >
            {label && <span className="cs-figure-label">{label}</span>}
            <div
              className={`cs-image-canvas${noCanvasBg ? ' cs-image-canvas--no-bg' : ''}${canvasVariant ? ` cs-image-canvas--${canvasVariant}` : ''}`}
              style={canvasColor ? { '--cs-canvas-bg': canvasColor } : undefined}
            >
              <GlassImage src={resolved} video={isVideo} />
            </div>
            {caption && <figcaption>{caption}</figcaption>}
          </figure>
        );
      })
    )
  );

  return (
    <section
      className={`cs-section cs-process-section${mediaFirst ? ' cs-process-section--media-first' : ''}${mediaOnly ? ' cs-process-section--media-only' : ''}`}
      id={section.omitAnchor ? undefined : section.id}
    >
      <TextRow eyebrow={section.title} title={section.heading}>
        {renderBody()}
        {typeof section.bullets?.[0] === 'string' && (
          <ul className="cs-bullet-list">
            {section.bullets.map((b, i) => (
              <li key={i} className="cs-bullet-item">{b}</li>
            ))}
          </ul>
        )}
        {section.subsections?.map((sub) => (
          <div key={sub.title} className="cs-work-block">
            <h3 className="cs-work-lead">{sub.title}</h3>
            {sub.text && <p className="cs-p">{sub.text}</p>}
            {sub.items?.length > 0 && (
              <ul className="cs-goal-list">
                {sub.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            )}
          </div>
        ))}
        {section.learnings?.map((l) => (
          <WorkBlock key={l.title} lead={l.title} body={l.text} />
        ))}
      </TextRow>

      {section.nextSteps?.length > 0 && (
        <div className="cs-next-row">
          <TextRow eyebrow="Next steps">
            {section.nextSteps.map((n) => (
              <WorkBlock key={n.title} lead={n.title} body={n.text} />
            ))}
          </TextRow>
        </div>
      )}

      {mediaFirst && renderMedia()}

      {section.overviewRows?.length > 0 && (
        <div className="cs-overview-rows">
          {section.overviewRows.map((row) => (
            <div key={row.label} className="cs-overview-row">
              <p className="cs-overview-row-label">{row.label}</p>
              <p className="cs-overview-row-text">{row.text}</p>
            </div>
          ))}
        </div>
      )}

      {(section.evidenceLead || section.evidenceBullets?.length > 0) && (
        <div className="cs-evidence">
          {section.evidenceLead && (
            <p className="cs-evidence-lead">{section.evidenceLead}</p>
          )}
          {section.evidenceBullets?.length > 0 && (
            <ul className="cs-evidence-list">
              {section.evidenceBullets.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {section.quotes?.length > 0 && (
        <div className="cs-research-quotes">
          {section.quotesLead && (
            <p className="cs-evidence-lead">{section.quotesLead}</p>
          )}
          <div className="cs-quote-list">
            {section.quotes.map((q) => (
              <blockquote key={q.title} className="cs-quote">
                <cite>{q.title}</cite>
                <p>&ldquo;{q.quote}&rdquo;</p>
              </blockquote>
            ))}
          </div>
        </div>
      )}

      {section.callout && !section.calloutAfterMedia && (
        <div className="cs-callout">
          <p className="cs-callout-label">{section.callout.label}</p>
          <p className="cs-callout-text">{section.callout.text}</p>
        </div>
      )}

      {section.findings?.length > 0 && (
        <div className="cs-findings">
          {section.findings.map((finding) => (
            <article key={finding.title} className="cs-finding">
              <h3 className="cs-finding-title">{finding.title}</h3>
              <p className="cs-finding-text">{finding.text}</p>
              {finding.quote && (
                <blockquote className="cs-quote cs-quote--inline">
                  <cite>{finding.quote.title}</cite>
                  <p>&ldquo;{finding.quote.text}&rdquo;</p>
                </blockquote>
              )}
            </article>
          ))}
        </div>
      )}

      {section.pivotContrast && (
        <div className="cs-pivot-contrast">
          <div className="cs-pivot-col">
            <p className="cs-pivot-label">{section.pivotContrast.beforeLabel || 'Before'}</p>
            <p className="cs-pivot-text">{section.pivotContrast.before}</p>
          </div>
          <span className="cs-pivot-arrow" aria-hidden="true">→</span>
          <div className="cs-pivot-col cs-pivot-col--after">
            <p className="cs-pivot-label">{section.pivotContrast.afterLabel || 'After'}</p>
            <p className="cs-pivot-text">{section.pivotContrast.after}</p>
          </div>
        </div>
      )}

      {section.closingLine && (
        <p className="cs-closing-line">{section.closingLine}</p>
      )}

      {section.flowSteps?.length > 0 && (
        <div className="cs-flow-steps">
          {section.flowSteps.map((step, index) => (
            <React.Fragment key={step}>
              <div className="cs-flow-step">{step}</div>
              {index < section.flowSteps.length - 1 && (
                <span className="cs-flow-arrow" aria-hidden="true">→</span>
              )}
            </React.Fragment>
          ))}
        </div>
      )}

      {section.footnote && (
        <p className="cs-footnote">{section.footnote}</p>
      )}

      {section.featureCards?.length > 0 && (
        <div className="cs-feature-grid">
          {section.featureCards.map((card) => (
            <div key={card.title} className="cs-feature-card">
              <div className="cs-feature-card-icon" aria-hidden="true">
                <FeatureCardIcon icon={card.icon} />
              </div>
              <h3 className="cs-feature-card-title">{card.title}</h3>
              <p className="cs-feature-card-text">{card.text}</p>
            </div>
          ))}
        </div>
      )}

      {(section.hmwLead || section.hmw) && (
        <div className="cs-hmw-block">
          {section.hmwLead && <p className="cs-hmw-lead">{section.hmwLead}</p>}
          {section.hmw && <p className="cs-hmw">{section.hmw}</p>}
        </div>
      )}

      {section.flowTracks?.length > 0 && (
        <div className="cs-flow-tracks">
          {section.flowTracks.map((track) => (
            <div key={track.title} className="cs-flow-track">
              <h3 className="cs-flow-track-title">{track.title}</h3>
              <div className="cs-flow-track-steps">
                {track.steps.map((step, index) => (
                  <React.Fragment key={`${track.title}-${step}`}>
                    <div className="cs-flow-step">{step}</div>
                    {index < track.steps.length - 1 && (
                      <span className="cs-flow-arrow" aria-hidden="true">→</span>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {(section.modelCards?.length > 0 || section.growthStages?.length > 0) && (
        <div className="cs-next-steps-layout">
          {section.modelCards?.length > 0 && (
            <div className="cs-model-card">
              {section.modelCards.map((block) => (
                <div key={block.label} className="cs-model-block">
                  <p className="cs-model-block-label">{block.label}</p>
                  {block.value && (
                    <p className="cs-model-block-value">{block.value}</p>
                  )}
                  {block.text && (
                    <p className="cs-model-block-text">{block.text}</p>
                  )}
                  {block.items?.length > 0 && (
                    <ul className="cs-model-block-list">
                      {block.items.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}
          {section.growthStages?.length > 0 && (
            <div className="cs-growth-path">
              {section.growthLead && (
                <p className="cs-growth-path-lead">{section.growthLead}</p>
              )}
              <div className="cs-growth-stages">
                {section.growthStages.map((item) => (
                  <div key={item.stage} className="cs-growth-stage">
                    <div className="cs-growth-circle">{item.stage}</div>
                    <p className="cs-growth-audience">{item.audience}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {section.bullets?.length > 0 && typeof section.bullets[0] !== 'string' && (
        (
          <div className="cs-summary-grid">
            {section.bullets.map((b) => (
              <div key={b.label} className={`cs-summary-tile cs-summary-tile--${b.label.toLowerCase()}`}>
                <div className="cs-summary-icon" aria-hidden="true">
                  {b.label === 'Problem' && (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="9" />
                      <path d="M12 8v5" />
                      <circle cx="12" cy="16.5" r="0.8" fill="currentColor" stroke="none" />
                    </svg>
                  )}
                  {b.label === 'Solution' && (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14.7 6.3a4.5 4.5 0 0 0-6.4 6.4l7 7 6.4-6.4a4.5 4.5 0 0 0-6.4-6.4z" />
                      <path d="M12.2 8.8l3 3" />
                    </svg>
                  )}
                  {b.label === 'Product' && (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="14" rx="2" />
                      <path d="M8 20h8" />
                      <path d="M12 18v2" />
                    </svg>
                  )}
                  {b.label === 'Goals' && (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 19V5" />
                      <path d="M4 19h16" />
                      <path d="M7 15l4-5 3 3 5-7" />
                    </svg>
                  )}
                </div>
                <div className="cs-summary-copy">
                  <p className="cs-summary-label">{b.label}</p>
                  <p className="cs-summary-text">{b.text}</p>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {section.challenges?.length > 0 && (
        <div className="cs-challenge-grid">
          {section.challenges.map((ch) => (
            <div key={ch.number} className="cs-challenge-card">
              <h3 className="cs-challenge-card-title">{ch.title}</h3>
              <p className="cs-challenge-card-text">{ch.text}</p>
            </div>
          ))}
        </div>
      )}

      {(section.issueItems?.length > 0 || section.solutionItems?.length > 0) && (
        <div className="cs-issues-solutions">
          {section.issueItems?.length > 0 && (
            <IssueSolutionBlock type="issues" title="Issues" items={section.issueItems} />
          )}
          {section.solutionItems?.length > 0 && (
            <IssueSolutionBlock type="solution" title="Solution" items={section.solutionItems} />
          )}
        </div>
      )}

      {!mediaFirst && renderMedia()}

      {section.annotated && <AnnotatedShot {...section.annotated} />}

      {section.callout && section.calloutAfterMedia && (
        <div className="cs-callout">
          <p className="cs-callout-label">{section.callout.label}</p>
          <p className="cs-callout-text">{section.callout.text}</p>
        </div>
      )}

      {section.problemStatements?.length > 0 && (
        <div className="cs-problem-statements" aria-label="Core problems">
          {section.problemStatements.map((item) => (
            <article key={item.title} className="cs-problem-statement">
              <h3 className="cs-problem-statement-title">{item.title}</h3>
              <p className="cs-problem-statement-text">{item.text}</p>
            </article>
          ))}
        </div>
      )}

      {section.placeholder && (
        <p className="cs-p cs-muted cs-process-placeholder">{section.placeholder}</p>
      )}
    </section>
  );
}

/* Title that comes into focus word by word (after angelinacao.com):
   each word starts transparent and blurred, then sharpens in turn. */
function BlurTitle({ text }) {
  const words = (text || '').split(' ');
  return (
    <h1 className="cs-min-title" aria-label={text}>
      {words.map((word, i) => (
        <React.Fragment key={`${word}-${i}`}>
          <span className="cs-min-word" aria-hidden="true" style={{ '--i': i }}>
            {word}
          </span>
          {i < words.length - 1 ? ' ' : ''}
        </React.Fragment>
      ))}
    </h1>
  );
}

function ProcessCaseStudy({ project, c, navigate }) {
  const heroSrc = project.heroImg || project.img;
  const heroIsVideo = heroSrc?.endsWith('.mp4');
  const impactNotes = c.impactNotes || [];
  const overviewSection = (c.process || []).find((s) => s.id === 'overview');
  const overview = overviewSection?.body || (c.context ? [c.context] : []);
  const sections = (c.process || []).filter((s) => s.id !== 'overview');
  const details = [
    ['Role', c.roles],
    ['Timeline', c.timeline],
    ['Team', c.team],
    ['Tools', c.tools],
  ].filter(([, value]) => value);

  return (
    <div className="cs-page cs-page--editorial cs-page--min">
      <button type="button" className="cs-min-back" onClick={() => navigate('/')}>
        <span aria-hidden="true">←</span> Return back home
      </button>
      <p className="cs-min-copy">©2026 Yaya Hagen</p>

      <main className="cs-content cs-min-col">
        <p className="cs-min-crumb">
          <button type="button" onClick={() => navigate('/')}>Home</button>
          {' / '}
          <span>{project.name}</span>
        </p>

        <BlurTitle text={c.title || project.name} />

        <div className="cs-min-intro">
          <div>
            <p className="cs-eyebrow">Details</p>
            {details.map(([label, value]) => (
              <p key={label} className="cs-min-detail">{label}: {value}</p>
            ))}
          </div>
          <div>
            <p className="cs-eyebrow">Overview</p>
            {overview.map((para, i) => (
              <p key={i} className="cs-p">{para}</p>
            ))}
          </div>
        </div>

        {heroSrc && (
          <div className="cs-ed-hero">
            {heroIsVideo ? (
              <video src={mediaSrc(heroSrc)} autoPlay loop muted playsInline />
            ) : (
              <img src={mediaSrc(heroSrc)} alt="" />
            )}
          </div>
        )}

        {impactNotes.length > 0 && (
          <section className="cs-ed-section" id="impact">
            <TextRow eyebrow={c.impactKicker || 'Impact'} title={c.impactHeading}>
              <ul className="cs-bullet-list">
                {impactNotes.map((note) => (
                  <li key={note} className="cs-bullet-item">{note}</li>
                ))}
              </ul>
            </TextRow>
          </section>
        )}

        {sections.map((section) => (
          <ProcessSection
            key={section.id}
            section={section}
            hideImageLabels={c.showImageLabels === false}
            defaultMediaFirst={false}
          />
        ))}

        <p className="cs-min-thanks">Thanks for being here.</p>
      </main>
    </div>
  );
}

function StandardCaseStudy({ project, c, navigate }) {
  const heroSrc = project.heroImg || project.img;
  const heroIsVideo = heroSrc?.endsWith('.mp4');
  const overviewParagraphs = useMemo(() => getOverviewParagraphs(c), [c]);
  const contextParagraphs = useMemo(
    () => uniqueParagraphs([
      ...overviewParagraphs,
      c.problem && !overviewParagraphs.includes(c.problem) ? c.problem : '',
    ]),
    [c.problem, overviewParagraphs]
  );
  const impactBlocks = useMemo(() => getImpactBlocks(project, c), [project, c]);

  return (
    <div className="cs-page cs-page--editorial">
      <div className="cs-layout">
        <CaseStudySidebar navigate={navigate} project={project} c={c} />

        <div className="cs-content">
          {heroSrc && (
            <div className="cs-ed-hero">
              {heroIsVideo ? (
                <video src={mediaSrc(heroSrc)} autoPlay loop muted playsInline />
              ) : (
                <img src={mediaSrc(heroSrc)} alt="" />
              )}
            </div>
          )}

          <h1 className="cs-ed-title">{c.title || project.name}</h1>

          {impactBlocks.length > 0 && (
            <section className="cs-ed-section" id="impact">
              <TextRow eyebrow="Impact">
                <ul className="cs-bullet-list">
                  {impactBlocks.map((h) => (
                    <li key={h.title} className="cs-bullet-item">{h.text}</li>
                  ))}
                </ul>
              </TextRow>
            </section>
          )}
          {contextParagraphs.length > 0 && (
            <section className="cs-ed-section" id="overview">
              <TextRow eyebrow="Overview">
                {contextParagraphs.map((para, i) => (
                  <p key={i} className="cs-p">{para}</p>
                ))}
              </TextRow>
              {c.roleImage && (
                <figure className="cs-figure cs-figure-role">
                  <img src={mediaSrc(c.roleImage)} alt="Yaya Hagen — UI/UX Designer" />
                </figure>
              )}
            </section>
          )}

          {c.problemImages?.length > 0 && (
            <section className="cs-ed-section" id="problem">
              <TextRow eyebrow="Problem" title={c.problemHeading} />
              {c.problemImages.map((img, i) => (
                <CaseFigure key={i} src={img} />
              ))}
              {c.unmoderatedQuotes?.length > 0 && (
                <>
                  <p className="cs-p cs-muted">{c.unmoderatedIntro || 'Users felt the same way.'}</p>
                  <div className="cs-quote-list">
                    {c.unmoderatedQuotes.map((q) => (
                      <blockquote key={q.title} className="cs-quote">
                        <cite>{q.title}</cite>
                        <p>&ldquo;{q.quote}&rdquo;</p>
                      </blockquote>
                    ))}
                  </div>
                </>
              )}
            </section>
          )}

          {(c.summary || c.solutionImage || c.screenDesignImage || c.dashboardFindings?.length > 0) && (
            <section className="cs-section cs-anchor-section" id="solutions">
              <TextRow eyebrow="Solutions" title={c.summary} />
              {c.solutionImage && <CaseFigure src={c.solutionImage} />}
              {c.dashboardFindings?.map((f) => (
                <WorkBlock key={f.title} lead={f.title} body={f.text} />
              ))}
              {c.logoImage && (
                <figure className="cs-figure cs-figure-logo" style={{ background: c.logoBg || '#f7f3ee' }}>
                  <img src={c.logoImage} alt="" />
                </figure>
              )}
              {(c.webLandingImage || c.posterImage) && (
                <div className="cs-duo">
                  {c.webLandingImage && <CaseFigure src={c.webLandingImage} caption="Web Landing Page" />}
                  {c.posterImage && <CaseFigure src={c.posterImage} caption="Poster Design" />}
                </div>
              )}
              {c.screenDesignImage && <CaseFigure src={c.screenDesignImage} />}
              {c.screenDesignNote && <p className="cs-p">{c.screenDesignNote}</p>}
            </section>
          )}

          <section className="cs-section cs-anchor-section" id="exploring">
              <TextRow eyebrow="Exploring" title={c.hmw}>
                {c.goals && (
                  <ul className="cs-bullet-list">
                    {c.goals.map((goal, i) => (
                      <li key={i} className="cs-bullet-item">
                        {typeof goal === 'object' ? (
                          <>{goal.emoji ? `${goal.emoji} ` : ''}{goal.text}</>
                        ) : (
                          goal
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                {c.marketLandscape && <p className="cs-p">{c.marketLandscape}</p>}
              </TextRow>
              {c.moodboardImages?.length > 0 && (
                <div className="cs-moodboard">
                  {c.moodboardImages.map((src) => (
                    <GlassImage key={src} src={mediaSrc(src)} />
                  ))}
                </div>
              )}
              {c.competitors?.length > 0 && (
                <div className={c.competitorsNoCard ? 'cs-competitors-flat' : 'cs-competitors'}>
                  {c.competitors.map((comp) => (
                    <div key={comp.name} className="cs-competitor">
                      <img src={comp.logo} alt={comp.name} />
                      <p>{comp.desc}</p>
                    </div>
                  ))}
                </div>
              )}
              {(c.v1Demo || c.v1Before) && (
                <>
                  <h3 className="cs-work-lead">{c.v1Summary || 'Design Version 1'}</h3>
                  {c.v1Before ? (
                    <div className="cs-before-after">
                      <CaseFigure src={c.v1Before} label="Before" />
                      <CaseFigure src={c.v1After} label="After" />
                    </div>
                  ) : (
                    <CaseFigure src={c.v1Demo} />
                  )}
                </>
              )}
              {(c.evalImage || c.moderatedFindings?.length > 0 || c.issueItems?.length > 0) && (
                <>
                  <h3 className="cs-work-lead">What testing sharpened</h3>
                  <p className="cs-p">
                    {c.moderatedIntro || c.evalSummary || 'Testing with users revealed where the experience broke down.'}
                  </p>
                  {c.issueItems?.length > 0 && (
                    <IssueSolutionBlock type="issues" title="Issues" items={c.issueItems} />
                  )}
                  {c.solutionItems?.length > 0 && (
                    <IssueSolutionBlock type="solution" title="Solution" items={c.solutionItems} />
                  )}
                  {c.evalImage && <CaseFigure src={c.evalImage} compact />}
                  {c.moderatedFindings?.map((f) => (
                    <WorkBlock key={f.title} lead={f.title} body={f.text} />
                  ))}
                </>
              )}
          </section>

          {c.learnings?.length > 0 && (
            <section className="cs-section cs-anchor-section" id="roadmap">
              <TextRow eyebrow="Roadmap" title={c.learningsHeading}>
                {c.learnings.map((l) => (
                  <WorkBlock
                    key={l.title}
                    lead={l.emoji ? `${l.emoji} ${l.title}` : l.title}
                    body={l.text}
                  />
                ))}
              </TextRow>
              {c.processImage && <CaseFigure src={c.processImage} />}
            </section>
          )}

          {project.href && (
            <p className="cs-outro">
              <a href={project.href} target="_blank" rel="noopener noreferrer">
                View full case study ↗
              </a>
            </p>
          )}

          <p className="cs-thanks">Thanks for reading.</p>
        </div>
      </div>
    </div>
  );
}

export default function CaseStudy() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const project = WORK_ITEMS.find(
    (w) => w.slug === slug || w.name.toLowerCase().replace(/\s+/g, '-') === slug
  );

  if (!project || !project.fullContent) {
    return (
      <div className="cs-not-found">
        <p>Project not found.</p>
        <button type="button" onClick={() => navigate('/')}>← Return back home</button>
      </div>
    );
  }

  if (project.fullContent.layout === 'process') {
    return <ProcessCaseStudy project={project} c={project.fullContent} navigate={navigate} />;
  }

  return <StandardCaseStudy project={project} c={project.fullContent} navigate={navigate} />;
}
